import React, { FC, useState } from "react";
import { Box, Stack, Typography, Chip, Tabs, Tab, Badge } from "@mui/material";
import { CheckCircle, Schedule, Cancel, TrendingUp } from "@mui/icons-material";
import { formatDateWithTime } from "../../utils/url-id";

interface ShiftDisplayProps {
  completedShifts: any[];
  upcomingShifts: any[];
  absentShifts: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ShiftDisplay: FC<ShiftDisplayProps> = ({
  completedShifts,
  upcomingShifts,
  absentShifts,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "completed":
        return {
          color: "#4CAF50",
          bgColor: "#E8F5E9",
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          label: "Completed",
        };
      case "upcoming":
        return {
          color: "#2196F3",
          bgColor: "#E3F2FD",
          icon: <Schedule sx={{ fontSize: 16 }} />,
          label: "Upcoming",
        };
      case "absent":
        return {
          color: "#F44336",
          bgColor: "#FFEBEE",
          icon: <Cancel sx={{ fontSize: 16 }} />,
          label: "Absent",
        };
      default:
        return {
          color: "#9E9E9E",
          bgColor: "#F5F5F5",
          icon: null,
          label: "Unknown",
        };
    }
  };

  const renderShiftCard = (shift: any, status: string) => {
    const config = getStatusConfig(status);

    return (
      <Box
        key={shift._id}
        sx={{
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          mb: 2,
          "&:last-child": {
            borderBottom: "none",
          },
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 1 }}
        >
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontFamily: "Poppins" }}
            >
              {shift?.medic_info?.name || "N/A"}
            </Typography>
            <Chip
              icon={config.icon as any}
              label={config.label}
              size="small"
              sx={{
                backgroundColor: config.bgColor,
                color: config.color,
                fontWeight: 600,
                fontSize: "11px",
                height: "24px",
                "& .MuiChip-icon": {
                  color: config.color,
                },
              }}
            />
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: "#757575", fontFamily: "Poppins" }}
          >
            {shift?.employee_info?.position || "N/A"}
          </Typography>
        </Stack>

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          spacing={3}
        >
          <Stack>
            <Typography
              variant="caption"
              sx={{ color: "#757575", fontWeight: 600 }}
            >
              Start Time
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "Poppins", fontWeight: 600 }}
            >
              {formatDateWithTime(shift.start_time)}
            </Typography>
          </Stack>

          <Stack>
            <Typography
              variant="caption"
              sx={{ color: "#757575", fontWeight: 600 }}
            >
              End Time
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "Poppins", fontWeight: 600 }}
            >
              {formatDateWithTime(shift.end_time)}
            </Typography>
          </Stack>

          {shift.clock_in_time && (
            <Stack>
              <Typography
                variant="caption"
                sx={{ color: "#757575", fontWeight: 600 }}
              >
                Clock In
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "Poppins", fontWeight: 600 }}
              >
                {formatDateWithTime(shift.clock_in_time)}
              </Typography>
            </Stack>
          )}

          {shift.clock_out_time && (
            <Stack>
              <Typography
                variant="caption"
                sx={{ color: "#757575", fontWeight: 600 }}
              >
                Clock Out
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "Poppins", fontWeight: 600 }}
              >
                {formatDateWithTime(shift.clock_out_time)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="shift tabs"
          sx={{
            "& .MuiTab-root": {
              fontFamily: "Poppins",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "15px",
            },
          }}
        >
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Completed</span>
                <Chip
                  label={completedShifts.length}
                  size="small"
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#4CAF50",
                    fontWeight: 700,
                    height: "20px",
                    fontSize: "12px",
                  }}
                />
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Upcoming</span>
                <Chip
                  label={upcomingShifts.length}
                  size="small"
                  sx={{
                    backgroundColor: "#E3F2FD",
                    color: "#2196F3",
                    fontWeight: 700,
                    height: "20px",
                    fontSize: "12px",
                  }}
                />
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Absent</span>
                <Chip
                  label={absentShifts.length}
                  size="small"
                  sx={{
                    backgroundColor: "#FFEBEE",
                    color: "#F44336",
                    fontWeight: 700,
                    height: "20px",
                    fontSize: "12px",
                  }}
                />
              </Stack>
            }
          />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        {completedShifts.length > 0 ? (
          completedShifts.map((shift) => renderShiftCard(shift, "completed"))
        ) : (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#757575", py: 4 }}
          >
            No completed shifts found
          </Typography>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {upcomingShifts.length > 0 ? (
          upcomingShifts.map((shift) => renderShiftCard(shift, "upcoming"))
        ) : (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#757575", py: 4 }}
          >
            No upcoming shifts found
          </Typography>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {absentShifts.length > 0 ? (
          absentShifts.map((shift) => renderShiftCard(shift, "absent"))
        ) : (
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#757575", py: 4 }}
          >
            No absent shifts found
          </Typography>
        )}
      </TabPanel>
    </Box>
  );
};

export default ShiftDisplay;
